// src/pages/lms/LMSLogin.jsx — FINAL RESTORED
// Admin login: lms_username + password
// Uses RPC get_auth_email_by_username to get Supabase auth email
// then signs in with signInWithPassword
// Staff login button → /lms/staff-login?switch=1
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function LMSLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  // Clear any staff session when admin login page loads
  useEffect(() => {
    localStorage.removeItem('lms_staff_session');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Enter your username and password');
      return;
    }
    setLoading(true); setError('');
    try {
      // Step 1: Get auth email from lms_username via RPC
      const { data: lookup, error: rpcErr } = await supabase.rpc(
        'get_auth_email_by_username',
        { p_username: username.trim().toLowerCase() }
      );

      if (rpcErr) throw rpcErr;
      if (!lookup?.found) {
        setError(lookup?.error || 'Username not found. Check your credentials.');
        setLoading(false); return;
      }

      // Step 2: Sign in with Supabase using auth email + password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email:    lookup.email,
        password: password,
      });

      if (authError) {
        setError('Incorrect password. Please try again.');
        setLoading(false); return;
      }

      // Step 3: Save institution_id for staff login page
      if (lookup.institution_id) {
        localStorage.setItem('lms_institution_id', lookup.institution_id);
      }

      window.location.href = '/lms/dashboard';
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToStaffLogin = () => {
    window.location.href = '/lms/staff-login?switch=1';
  };

  const INP = {
    width: '100%', padding: '11px 14px', borderRadius: 10,
    border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none',
    color: '#111827', boxSizing: 'border-box', background: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, margin: '0 auto 16px',
            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(79,142,247,0.4)',
          }}>
            <span style={{ fontSize: 28 }}>🏫</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 900, margin: 0 }}>
            UNIVERSE LMS
          </h1>
          <p style={{ color: '#94A3B8', fontSize: 14, margin: '6px 0 0' }}>
            Institution Admin Portal
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 24, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          {/* Admin form */}
          <div style={{ padding: '32px 32px 24px' }}>
            <h2 style={{ margin: '0 0 6px', fontWeight: 800, color: '#111827', fontSize: 20 }}>
              Admin Login
            </h2>
            <p style={{ color: '#6B7280', fontSize: 13, margin: '0 0 24px' }}>
              Sign in to manage your institution
            </p>

            {error && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderRadius: 10, padding: '11px 14px', marginBottom: 16,
                fontSize: 13, color: '#991B1B', fontWeight: 600,
              }}>
                ❌ {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. trent_university"
                  autoComplete="username"
                  style={INP}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={INP}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 12, border: 'none',
                  background: loading ? '#E5E7EB' : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  color: loading ? '#9CA3AF' : '#fff',
                  fontSize: 15, fontWeight: 800,
                  cursor: loading ? 'wait' : 'pointer',
                }}>
                {loading ? 'Signing in...' : 'Sign In as Admin'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#6B7280' }}>
              Don't have an account?{' '}
              <Link to="/lms/register" style={{ color: '#4F8EF7', fontWeight: 600 }}>
                Register your institution
              </Link>
            </p>
            <p style={{ textAlign: 'center', marginTop: 6, fontSize: 12, color: '#9CA3AF' }}>
              Contact{' '}
              <a href="mailto:admin@inncatechnologies.com" style={{ color: '#4F8EF7' }}>
                admin@inncatechnologies.com
              </a>{' '}
              for support.
            </p>
          </div>

          {/* Staff login — outside form */}
          <div style={{
            borderTop: '1px solid #F1F5F9',
            padding: '20px 32px 28px',
            background: '#FAFBFF',
          }}>
            <p style={{ textAlign: 'center', fontSize: 13, color: '#6B7280', margin: '0 0 12px', fontWeight: 500 }}>
              Not an admin?
            </p>
            <button
              onClick={goToStaffLogin}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 12,
                border: '2px solid #7C3AED', background: '#F5F3FF',
                color: '#7C3AED', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              👤 I'm a Staff Member / Teacher
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: '#475569' }}>
          UNIVERSE LMS · Innca Technologies
        </div>
      </div>
    </div>
  );
}